import { format } from "date-fns";

import { getFormattedTimezone } from "./timezone";

const today = new Date();
today.setHours(0, 0, 0, 0);
const currentDay = today.getDate();
const currentMonth = today.toLocaleDateString("en-US", { month: "long" });
const currentDayName = today.toLocaleDateString("en-US", { weekday: "long" });
const timezoneFormat = 60;
const timezoneFormatted = `GMT${today.getTimezoneOffset() / -timezoneFormat}`;

const parseDateTime = (dateTimeString: string): Date => {
  const parts = dateTimeString.split(" ");
  const iso = parts[2]
    ? `${parts[0]}T${parts[1]}${parts[2]}`
    : `${parts[0]}T${parts[1]}`;
  return new Date(iso);
};

/**
 * True once an appointment slot's start time has passed.
 *
 * Uses the same parse as the slot formatters so the check and the label can never
 * disagree about which instant a slot string names. The backend already withholds
 * slots inside its minimum-notice buffer at fetch time; this only catches time
 * passing while the page stays open, so it compares against the bare instant —
 * the same hard floor booking enforces.
 */
const isSlotInPast = (
  dateTimeString: string,
  now: number = Date.now(),
): boolean => {
  const slotTime = parseDateTime(dateTimeString).getTime();

  return !isNaN(slotTime) && slotTime <= now;
};

/**
 * How many of these slots are still bookable right now.
 *
 * The card renders a filtered list, so an unfiltered `.length` in a tracking payload
 * reports a number the patient was never shown — and the gap grows the longer they
 * sit on the page. Takes the bare instant like isSlotInPast: the server already
 * applied its notice buffer when it answered.
 */
const countBookableSlots = (
  slots: string[],
  now: number = Date.now(),
): number => slots.filter((slot) => !isSlotInPast(slot, now)).length;

const formatAppointmentTime = (dateTimeString?: string) => {
  if (!dateTimeString) return "";

  const date = parseDateTime(dateTimeString);

  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const formatAppointmentTimeWithTimezone = (dateTimeString?: string) => {
  if (!dateTimeString) return "";
  const date = parseDateTime(dateTimeString);
  return `${format(date, "hh:mm a")} ${getFormattedTimezone()}`;
};

const formatAppointmentDate = (dateTimeString?: string) => {
  if (!dateTimeString) return "";
  const date = parseDateTime(dateTimeString);
  if (isNaN(date.getTime())) return "";
  return format(date, "EEEE MM/dd/yyyy");
};

/**
 * Append the local UTC offset to a datetime string that has no offset.
 * Cached slots come as "2026-04-07 14:00:00" — the API expects "2026-04-07 14:00:00 -0400".
 *
 * The offset MUST come from the appointment's own date, not from today. Using
 * today's offset sends the wrong instant for any booking that straddles a DST
 * transition — e.g. booking a November slot in September stamps -0400 on a date
 * that is actually -0500, shifting the appointment an hour. With a 60-day
 * lookahead that window is wide, and the backend now matches booking timestamps
 * against cached ET slot times, so an hour of drift means no matching slot.
 */
const formatDateTimeWithOffset = (dateTime: string): string => {
  // Already has an offset (e.g., "-0600", "+0400") — return as-is
  if (/[+-]\d{4}$/.test(dateTime.trim())) {
    return dateTime;
  }

  // Parsed without an offset, this is interpreted in the viewer's local zone, so
  // getTimezoneOffset() reports the offset in effect ON THAT DATE.
  const trimmed = dateTime.trim();
  const atAppointmentDate = new Date(trimmed.replace(" ", "T"));
  const reference = isNaN(atAppointmentDate.getTime())
    ? new Date()
    : atAppointmentDate;

  const offsetMinutes = reference.getTimezoneOffset();
  const sign = offsetMinutes <= 0 ? "+" : "-";
  const absMinutes = Math.abs(offsetMinutes);
  const hours = String(Math.floor(absMinutes / 60)).padStart(2, "0");
  const mins = String(absMinutes % 60).padStart(2, "0");
  // Append to the trimmed value, not the raw one: trailing whitespace would produce a
  // double space, which StoreAppointmentRequest's date_format rule rejects with a 422.
  return `${trimmed} ${sign}${hours}${mins}`;
};

export {
  countBookableSlots,
  currentDay,
  currentDayName,
  currentMonth,
  formatAppointmentDate,
  formatAppointmentTime,
  formatAppointmentTimeWithTimezone,
  formatDateTimeWithOffset,
  isSlotInPast,
  timezoneFormatted,
  today,
};
